// src/pages/Manager/Steps.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  New. The per step manager layer, from the Trainer Edition.
//
// The same thirteen steps a salesperson learns, read from the other chair.
// A rep opening step 4 gets the teaching and the word tracks. A manager opening
// it gets what to watch for on the floor, how to coach the miss, a role play
// they can run at the desk, and the criteria they will be signing off against.
//
// One expanded at a time, because thirteen steps of four sections each is a
// wall, and a manager arrives here about one step rather than about all of them.

import { useState } from 'react'
import { Box, HStack, VStack, Text, Collapse, Badge } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronLeft, ChevronDown, ChevronRight } from 'lucide-react'
import { MANAGER_PER_STEP } from '../../content/manager'
import { STEPS } from '../../content/steps'

function titleFor(number) {
  return STEPS.find((s) => s.number === number)?.title || `Step ${number}`
}

function Section({ label, items }) {
  if (!items || items.length === 0) return null
  return (
    <Box mt={5}>
      <Text textStyle="eyebrow" color="inkDim" mb={2}>
        {label}
      </Text>
      <VStack align="stretch" spacing={2}>
        {items.map((t) => (
          <HStack key={t} align="flex-start" spacing={2.5}>
            <Box w="4px" h="4px" borderRadius="1px" bg="accent" mt="9px" flexShrink={0} />
            <Text fontSize="body" lineHeight={1.6} maxW="measure">
              {t}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  )
}

function Lab({ lab }) {
  return (
    <Box mt={3} borderLeft="3px solid" borderColor="line" pl={4} py={1}>
      <Text fontWeight={600} fontSize="body">
        {lab.title}
      </Text>
      <Text fontSize="body" color="inkMuted" mt={1} maxW="measure" lineHeight={1.6}>
        {lab.scenario}
      </Text>
    </Box>
  )
}

function StepRow({ step, open, onToggle }) {
  return (
    <Box borderTop="1px solid" borderColor="line">
      <HStack
        as="button"
        type="button"
        onClick={onToggle}
        w="100%"
        align="center"
        spacing={4}
        py={4}
        textAlign="left"
        _hover={{ '& .chev': { color: 'ink' } }}
      >
        <Text fontFamily="mono" fontSize="sm" color="inkDim" w="1.6rem" flexShrink={0}>
          {String(step.number).padStart(2, '0')}
        </Text>
        <Text flex={1} fontWeight={600} letterSpacing="-0.01em">
          {titleFor(step.number)}
        </Text>
        {step.roleplayLabs?.length > 0 && <Badge variant="subtle">lab</Badge>}
        <Box className="chev" color="inkDim" flexShrink={0}>
          {open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
        </Box>
      </HStack>

      <Collapse in={open} animateOpacity>
        <Box pb={7} pl={{ base: 0, md: '3.4rem' }}>
          <Section label="What to watch for" items={step.managerCheckpoints} />
          <Section label="How to coach it" items={step.coachingNotes} />
          {step.roleplayLabs?.length > 0 && (
            <Box mt={5}>
              <Text textStyle="eyebrow" color="inkDim" mb={2}>
                Run this at the desk
              </Text>
              {step.roleplayLabs.map((lab) => (
                <Lab key={lab.title} lab={lab} />
              ))}
            </Box>
          )}
          <Section label="Signing off against" items={step.signoffCriteria} />
        </Box>
      </Collapse>
    </Box>
  )
}

export default function ManagerSteps() {
  const [open, setOpen] = useState(null)

  return (
    <VStack align="stretch" spacing={7} maxW="sheet">
      <HStack
        as={RouterLink}
        to="/manager/"
        spacing={2}
        color="inkMuted"
        fontSize="body-sm"
        alignSelf="flex-start"
        _hover={{ textDecoration: 'none', color: 'ink' }}
      >
        <ChevronLeft size={15} />
        <Text>Running the floor</Text>
      </HStack>

      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          The thirteen steps
        </Text>
        <Text textStyle="pageTitle">From your side</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          Your reps are learning the words. You are watching whether the outcome happened, which is a
          different job and needs different notes.
        </Text>
      </Box>

      <Box>
        {MANAGER_PER_STEP.map((step) => (
          <StepRow
            key={step.number}
            step={step}
            open={open === step.number}
            onToggle={() => setOpen(open === step.number ? null : step.number)}
          />
        ))}
      </Box>
    </VStack>
  )
}
